<!--
  Aviso ambar de conflito de agenda ao criar/editar um plantao.

  Hierarquia visual do painel:
  - AMBAR / "REQUER ATENCAO" (este bloco): informativo, NAO bloqueia. O backend detecta a
    sobreposicao dentro da transacao de escrita e devolve `overlapAlerts` com a vaga ja
    gravada — por isso o texto e a posteriori ("A vaga foi criada, mas...").
  - VERMELHO / "IMPEDE" (`ConsecutiveHoursBlockAlert`): bloqueio duro, impede o submit.

  Nao reaproveita `AlertMessagesSection.vue` (orfao) porque aquele bloco e vermelho
  (`bg-negative`), reservado ao nivel que impede.
-->
<template>
  <div
    v-if="alerts.length > 0"
    data-testid="schedule-overlap-alert"
    class="flex flex-col gap-3 rounded-lg border border-alert bg-alert/10 p-3 text-left"
  >
    <div class="flex items-center gap-2">
      <!-- `mdiAlertCircle` recebe cor por prop, entao o token `alert` vai como hex. -->
      <alert-circle :color="ALERT_TOKEN_HEX" width="18" height="18" class="shrink-0" />
      <span
        data-testid="schedule-overlap-alert-badge"
        class="rounded bg-alert px-2 py-[2px] text-[11px] font-bold uppercase leading-4 tracking-wide text-white"
      >
        Requer atenção
      </span>
    </div>

    <p class="text-sm leading-[18px] text-textPrimary">
      <span class="font-bold">Conflito de agenda:</span>
      {{ headline }}
    </p>

    <div
      v-for="(alert, index) in alerts"
      :key="index"
      data-testid="schedule-overlap-alert-item"
      class="flex flex-col gap-1"
    >
      <p v-if="alert.description" class="text-sm leading-[18px] text-textPrimary">
        {{ alert.description }}
      </p>
      <p
        v-if="formatAlertDate(alert)"
        data-testid="schedule-overlap-alert-date"
        class="text-sm font-bold leading-[18px] text-textPrimary"
      >
        {{ formatAlertDate(alert) }}
      </p>
    </div>

    <p class="text-sm leading-[18px] text-textPrimary">
      Nada foi bloqueado. Revise a agenda do médico se a sobreposição não for intencional.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import AlertCircle from '@/assets/icons/mdiAlertCircle.vue'
import type { Alert } from '@/Network/Types/Responses/StationResponses'

/** Espelha o token `alert` do tailwind.config.js (paleta de aviso do design system). */
const ALERT_TOKEN_HEX = '#F18F1B'

const props = withDefaults(
  defineProps<{
    alerts: Alert[]
    /** Muda apenas o verbo do aviso a posteriori: a vaga ja foi gravada nos dois casos. */
    mode?: 'create' | 'update'
  }>(),
  {
    mode: 'create'
  }
)

const headline = computed(() =>
  props.mode === 'update'
    ? 'A edição foi salva, mas o médico já está alocado em outro plantão neste horário.'
    : 'A vaga foi criada, mas o médico já está alocado em outro plantão neste horário.'
)

function formatAlertDate(alert: Alert): string {
  const value = alert.dateTime
  if (!value) return ''
  const parsed = typeof value === 'string' ? parseISO(value) : value
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return ''
  return `Plantão em conflito: ${format(parsed, 'dd/MM/yyyy')}`
}
</script>
